import { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import Drawer from "../Drawer";
import { catalogViews, COLUMN_GROUP_ORDER, DEFAULT_COLUMNS, GROUP_LABELS } from "../../consts/productCatalog";

const mergeColumnsWithBase = (columns = [], baseColumns = DEFAULT_COLUMNS) => {
  const columnMap = new Map((columns || []).map((column) => [column.id, column]));
  return baseColumns.map((baseColumn) => ({
    ...baseColumn,
    ...(columnMap.get(baseColumn.id) || {}),
  }));
};

const groupColumns = (columns) => {
  const byGroup = {};
  for (const col of columns) {
    if (!byGroup[col.group]) byGroup[col.group] = [];
    byGroup[col.group].push(col);
  }
  return byGroup;
};

export const ProductCatalogDefaultsSettings = ({
  isOpen,
  onClose,
  defaultSettings,
  viewPresets,
  onSave,
  onSaveViewPresets,
  tierDiscountDrivers,
  demandTierPresets = [],
  onSaveTierDiscountDrivers,
  embedded = false,
  /** When set, only that block is shown (hub sub-panels). */
  sectionFilter = null,
}) => {
  const [draftColumns, setDraftColumns] = useState(() =>
    mergeColumnsWithBase(defaultSettings?.default_columns)
  );
  const [draftViewPresets, setDraftViewPresets] = useState({});
  const [draftPageSize, setDraftPageSize] = useState(
    defaultSettings?.default_pagination_pages || 50
  );
  const [activeViewPreset, setActiveViewPreset] = useState("");
  const [showViewPresets, setShowViewPresets] = useState(false);
  const [showDefaultColumns, setShowDefaultColumns] = useState(false);
  const [draftTierDiscountDrivers, setDraftTierDiscountDrivers] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setDraftColumns(mergeColumnsWithBase(defaultSettings?.default_columns));
    setDraftViewPresets(
      Object.fromEntries(
        catalogViews.map((view) => [
          view.value,
          mergeColumnsWithBase(
            viewPresets?.[view.value],
            defaultSettings?.default_columns || DEFAULT_COLUMNS
          ),
        ])
      )
    );
    setDraftPageSize(defaultSettings?.default_pagination_pages || 50);
    setDraftTierDiscountDrivers(tierDiscountDrivers?.by_demand_tier_id || {});
    setActiveViewPreset("");
    if (sectionFilter === "view_presets") setShowViewPresets(true);
    if (sectionFilter === "default_columns") setShowDefaultColumns(true);
  }, [defaultSettings, isOpen, viewPresets, sectionFilter, tierDiscountDrivers]);

  const groupedColumns = useMemo(
    () => groupColumns(draftColumns),
    [draftColumns]
  );
  const activePresetColumns = draftViewPresets[activeViewPreset] || mergeColumnsWithBase(
    viewPresets?.[activeViewPreset],
    defaultSettings?.default_columns || DEFAULT_COLUMNS
  );
  const groupedPresetColumns = useMemo(
    () => groupColumns(activePresetColumns),
    [activePresetColumns]
  );

  const visibleColumnsCount = draftColumns.filter((column) => column.visible).length;
  const activePresetVisibleCount = activePresetColumns.filter((column) => column.visible).length;

  const handleToggleColumn = (columnId) => {
    setDraftColumns((prev) =>
      prev.map((column) => {
        if (column.id !== columnId) return column;
        if (column.pinned === "left") return column;
        return { ...column, visible: !column.visible };
      })
    );
  };

  const handleTogglePresetColumn = (columnId) => {
    setDraftViewPresets((prev) => ({
      ...prev,
      [activeViewPreset]: (prev[activeViewPreset] || []).map((column) => {
        if (column.id !== columnId) return column;
        if (column.pinned === "left") return column;
        return { ...column, visible: !column.visible };
      }),
    }));
  };

  const showViewPresetsBlock = !sectionFilter || sectionFilter === "view_presets";
  const showDefaultColumnsBlock = !sectionFilter || sectionFilter === "default_columns";
  const showPageSizeBlock = !sectionFilter;
  const showTierDiscountDriversBlock = !sectionFilter;
  const visibleDemandTierPresets = demandTierPresets.filter(
    (preset) => String(preset?.name || "").trim().toUpperCase() !== "NONE"
  );

  const footerEl = (
    <div className="py-0 bg-white">
      <button
        type="button"
        onClick={() =>
          onSave({
            default_columns: draftColumns,
            default_pagination_pages: draftPageSize,
          })
        }
        className="w-full inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-primary shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        Save Default Settings
      </button>
    </div>
  );

  const inner = (
      <div className="flex flex-col gap-3">
        <section className="flex flex-col gap-3">
          {!sectionFilter ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Views & Columns
            </p>
          ) : null}
          {showViewPresetsBlock && (
          <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setShowViewPresets((prev) => !prev)}
              className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-all duration-200"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-medium text-gray-900">View Presets</p>
                  <span className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-500">
                    {catalogViews.length}
                  </span>
                </div>
                {showViewPresets ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </button>
            {showViewPresets && (
              <div className="border-t border-gray-200 px-4 py-4 flex flex-col gap-4 bg-gray-50/40">
              {catalogViews.map((view) => {
                const presetColumns = draftViewPresets[view.value] || [];
                const selectedCount = presetColumns.filter((column) => column.visible).length;
                const isExpanded = activeViewPreset === view.value;

                return (
                  <div
                    key={view.value}
                    className="rounded-md border border-gray-200 bg-white overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setActiveViewPreset((prev) =>
                          prev === view.value ? "" : view.value
                        )
                      }
                      className="w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-gray-50 transition-all duration-200"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {view.label}
                        </p>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {selectedCount} cols
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <PencilSquareIcon className="h-4 w-4" />
                        {isExpanded ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-200 px-3 py-3 flex flex-col gap-4 bg-gray-50/40">
                        {COLUMN_GROUP_ORDER.map((groupKey) => {
                          const groupCols = groupedPresetColumns[groupKey];
                          if (!groupCols?.length) return null;
                          return (
                            <section key={groupKey} className="flex flex-col gap-2">
                              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                {GROUP_LABELS[groupKey] ?? groupKey}
                              </h3>
                              <div className="flex flex-wrap gap-2">
                                {groupCols.map((column) => {
                                  const isPinned = column.pinned === "left";
                                  const isVisible = column.visible;
                                  return (
                                    <button
                                      key={column.id}
                                      type="button"
                                      disabled={isPinned}
                                      onClick={() => handleTogglePresetColumn(column.id)}
                                      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-all duration-200 ${
                                        isPinned
                                          ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                          : isVisible
                                            ? "border-primary bg-primary text-white"
                                            : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                                      }`}
                                    >
                                      {column.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </section>
                          );
                        })}

                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-gray-500">
                            {selectedCount} columns selected
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              onSaveViewPresets(draftViewPresets);
                              setActiveViewPreset("");
                            }}
                            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
                          >
                            Save Preset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
          )}
          {showDefaultColumnsBlock && (
          <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setShowDefaultColumns((prev) => !prev)}
              className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-all duration-200"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Default Visible Columns</p>
                  <span className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-500">
                    {visibleColumnsCount}
                  </span>
                </div>
                {showDefaultColumns ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </button>
            {showDefaultColumns && (
              <div className="border-t border-gray-200 px-4 py-4 flex flex-col gap-5 bg-gray-50/40">
              {COLUMN_GROUP_ORDER.map((groupKey) => {
                const groupCols = groupedColumns[groupKey];
                if (!groupCols?.length) return null;
                return (
                  <section key={groupKey} className="flex flex-col gap-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {GROUP_LABELS[groupKey] ?? groupKey}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {groupCols.map((column) => {
                        const isPinned = column.pinned === "left";
                        const isVisible = column.visible;
                        return (
                          <button
                            key={column.id}
                            type="button"
                            disabled={isPinned}
                            onClick={() => handleToggleColumn(column.id)}
                            className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-all duration-200 ${
                              isPinned
                                ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                : isVisible
                                  ? "border-primary bg-primary text-white"
                                  : "border-gray-300 bg-white text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {column.label}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
              <p className="text-xs text-gray-500">
                Pinned columns are always visible and cannot be toggled.
              </p>
            </div>
            )}
          </div>
          )}
        </section>

        {showPageSizeBlock && (
        <section className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Defaults
          </p>
          <div className="rounded-md border border-gray-200 bg-white px-4 py-3 flex items-center justify-between gap-4">
            <p className="text-sm font-medium text-gray-900">Default Page Size</p>
            <div className="flex items-center gap-1">
              {[25, 50, 100, 200].map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => setDraftPageSize(size)}
                  className={`rounded px-3 py-1 text-xs font-medium transition-all duration-200 border ${
                    draftPageSize === size
                      ? "bg-primary text-white"
                      : "bg-white text-gray-500 border-gray-200"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </section>
        )}
        {showTierDiscountDriversBlock && (
        <section className="flex flex-col gap-3">
          <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-3">
              <p className="text-sm font-medium text-gray-900">Tier Discount Drivers</p>
              <button
                type="button"
                onClick={() => onSaveTierDiscountDrivers?.({ by_demand_tier_id: draftTierDiscountDrivers })}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-primary shadow-sm hover:bg-gray-50"
              >
                Save
              </button>
            </div>
            <div className="border-t border-gray-200 bg-gray-50/40 px-4 py-3">
              <div className="flex flex-col gap-2">
                {visibleDemandTierPresets.map((preset) => (
                  <label key={preset.id} className="flex items-center justify-between gap-3 text-sm text-gray-700">
                    <span>{preset.name}</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={draftTierDiscountDrivers[String(preset.id)] ?? ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          setDraftTierDiscountDrivers((current) => ({
                            ...current,
                            [String(preset.id)]: value === "" ? null : Number(value),
                          }));
                        }}
                        className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>
        )}
      </div>
  );

  if (embedded) {
    return isOpen ? (
      <>
        {inner}
        {footerEl}
      </>
    ) : null;
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Default Settings"
      width="500px"
      footer={footerEl}
    >
      {inner}
    </Drawer>
  );
};
